import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Home.scss';
import { useCookies } from 'react-cookie';
import { useAppDispatch } from '../../hooks/redux';
import { NewsPromotions } from '../../components/homeParts/NewsPromotions';
import { BrizTvPromo } from '../../components/homeParts/BrizTvPromo';
import { BrizTvWatch } from '../../components/homeParts/BrizTvWatch';
import { OurApps } from '../../components/homeParts/OurApps';
import { CookieItems, ModalVariants, LocalStorageItems } from '../../models/Common';
import { ServiceCards } from '../../components/homeParts/ServiceCards';
import { userInfoApi } from '../../services/apiServices/user/userInfo';
import {
  brizTvInterviewAvailable,
  suspensionOfServicesIds,
  brizTvShareId,
  isBrizTvFeatureEnabled,
} from '../../constants/configuration';
import { useFindShare } from '../../hooks/useFindShare';
import { modalSlice } from '../../store/reducers/ModalSlice';
import { ServicesTypes } from '../../models/Accounts';

export const Home: FC = () => {
  const controlledAccount = localStorage.getItem(LocalStorageItems.ControlledAccount);
  const { useGetUserInfoQuery, useGetUserInfoMultiAccQuery } = userInfoApi;
  const { data: userInfoState, isSuccess: isUserInfoDownloaded } = controlledAccount
    ? useGetUserInfoMultiAccQuery(
        { subAccountId: JSON.parse(controlledAccount).UserID },
        { refetchOnMountOrArgChange: true },
      )
    : useGetUserInfoQuery('', { refetchOnMountOrArgChange: true });
  const { share: brizTvShare, isUserInfoDownloaded: isShareLoaded } = useFindShare(brizTvShareId);
  const { t } = useTranslation();
  const isBrizTvShareAvailable = userInfoState?.Permissions.Services.Iptv.BrizTVAvailable;
  const { data: connectedAccounts, isFetching: isLoadingConnectedAccounts } =
    userInfoApi.useGetConnectedAccountsQuery('');
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [cookie] = useCookies([`${CookieItems.INTERVIEW}_${userInfoState?.id}`]);

  const connectedAccountsList = Object.keys(connectedAccounts || {})
    .map((key) => connectedAccounts?.[key] || [])
    .flat();

  const isBrizTvAvailableAndActive = connectedAccounts?.union_accounts?.some(
    (el) =>
      (el.Service.Type?.id === ServicesTypes.INTERNET_WITH_IPTV && el.StatusID === 1) ||
      (el.Service.Type?.id === ServicesTypes.INTERNET_TV_MAX && el.StatusID === 1),
  );

  const isBrizTvAccountExist =
    !!(connectedAccounts?.union_accounts?.filter((el) => el?.Iptv?.BrizTV !== null)[0]?.Iptv?.BrizTV || null) ||
    !!(connectedAccounts?.iptv_accounts?.filter((el) => el?.BrizTV !== null)[0]?.BrizTV || null);

  const isServiceExAvailable = !!(
    connectedAccounts?.union_accounts?.filter((el) => el?.Iptv?.BrizTV !== null && el?.Iptv?.ServicesEx?.length)[0]
      ?.Iptv?.BrizTV || null
  );

  const areAvailableBrizTvCards =
    connectedAccountsList.filter((el) => el.Service?.Daily || suspensionOfServicesIds.includes(el.Service?.id)).length <
      (connectedAccounts?.union_accounts?.filter((el) => !!el.Iptv) || []).length &&
    (connectedAccounts?.union_accounts?.filter((el) => !!el.Iptv) || []).length > 0;

  useEffect(() => {
    if (
      !isLoadingConnectedAccounts &&
      isUserInfoDownloaded &&
      brizTvInterviewAvailable !== 'false' &&
      isBrizTvAccountExist &&
      isBrizTvAvailableAndActive &&
      (!cookie[`${CookieItems.INTERVIEW}_${userInfoState.id}`] ||
        +cookie[`${CookieItems.INTERVIEW}_${userInfoState.id}`] !== userInfoState.id)
    ) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.InterviewRegistration,
          content: { userId: userInfoState.id },
        }),
      );
    }
  }, [isLoadingConnectedAccounts, userInfoState]);

  if (!isUserInfoDownloaded && userInfoState?.Name) {
    return (
      <div className="home-page page">
        <div className="home-page__container container">
          <p>{t('system.errorRefreshPage')}</p>
        </div>
      </div>
    );
  }

  const isAvailableBrizTvPromo =
    isShareLoaded && !brizTvShare && isBrizTvShareAvailable && areAvailableBrizTvCards && !isServiceExAvailable;

  return (
    <div className="home-page page">
      <div className="home-page__container container">
        <ServiceCards connectedAccounts={connectedAccounts} isLoadingConnectedAccounts={isLoadingConnectedAccounts} />
        {isBrizTvFeatureEnabled && isAvailableBrizTvPromo && <BrizTvPromo />}
        {isBrizTvFeatureEnabled && !isAvailableBrizTvPromo && userInfoState?.TypeID === 1 && <BrizTvWatch />}
        <NewsPromotions />
        <OurApps />
      </div>
    </div>
  );
};
